import { useMemo } from "react";
import { Flex, Text, Spinner } from "@radix-ui/themes";
import * as Dialog from "@radix-ui/react-dialog";
import { IoMdClose } from "react-icons/io";
import { useTrackAssetsControllerGetTrackLyrics } from "@/lib/lutely-api/gen/track-assets/track-assets";
import { getLyricsLines } from "@/lib/utils/lyrics-utils";

type AlbumLyricsDrawerProps = {
  trackId: string | null;
  /** Every bad word masked, for the Clean version. */
  clean?: boolean;
  trackTitle: string;
  onClose: () => void;
};

export default function AlbumLyricsDrawer({
  trackId,
  clean,
  trackTitle,
  onClose,
}: AlbumLyricsDrawerProps) {
  const open = !!trackId;

  // Uncensored unless clean: only the always-masked words stay hidden.
  const { data: lyricsData, isLoading } =
    useTrackAssetsControllerGetTrackLyrics(
      { trackId: trackId || "", ...(clean ? {} : { unmasked: true }) },
      { query: { enabled: !!trackId } },
    );

  const lyricsLines = useMemo(() => getLyricsLines(lyricsData), [lyricsData]);
  const hasLyrics = lyricsLines.length > 0;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="drawer-wrapper fixed right-0 top-0 h-full w-full sm:w-[480px] bg-[#0a0a12]/95 backdrop-blur-md border-l border-white/[0.06] z-50 flex flex-col shadow-xl">
          {/* Green accent bar */}
          <div className="h-[2px] w-full bg-[#43DDA7] shrink-0" />

          {/* Header */}
          <Flex direction="column" className="gap-1 px-7 pt-5 pb-4 shrink-0">
            <Flex className="items-center justify-between">
              <Text className="text-[12px] font-medium uppercase tracking-[0.1em] text-white/50">
                Lyrics
              </Text>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.07] hover:bg-white/[0.14] transition-colors duration-200 cursor-pointer shrink-0"
                  aria-label="Close lyrics"
                >
                  <IoMdClose className="text-[18px] text-white/50" />
                </button>
              </Dialog.Close>
            </Flex>
            <Dialog.Title asChild>
              <Text className="text-[20px] font-semibold text-white tracking-[-0.01em] truncate pr-12">
                {trackTitle}
              </Text>
            </Dialog.Title>
          </Flex>

          {/* Divider */}
          <div className="h-[1px] w-full bg-white/[0.08] shrink-0" />

          {/* Content with scroll fade */}
          <div className="relative flex-1 min-h-0">
            <div className="h-full overflow-y-auto px-7 pt-8 pb-20">
              {isLoading && (
                <Flex className="items-center justify-center h-32">
                  <Spinner size="2" />
                </Flex>
              )}

              {!isLoading && !hasLyrics && (
                <Flex className="items-center justify-center h-32">
                  <Text className="text-[14px] text-white/30 font-normal">
                    No lyrics detected
                  </Text>
                </Flex>
              )}

              {!isLoading && hasLyrics && (
                <Flex direction="column">
                  {lyricsLines.map((line, index) =>
                    line === "" ? (
                      <div key={index} className="h-6" />
                    ) : (
                      <Text
                        key={index}
                        className="text-[16px] leading-[2.2] text-white/65 font-normal"
                      >
                        {line}
                      </Text>
                    ),
                  )}
                </Flex>
              )}
            </div>

            {/* Bottom fade gradient */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#0a0a12] to-transparent pointer-events-none" />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
